// src/app/(plataforma)/financas/page.jsx
import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  Divider,
} from "@mui/material";
import {
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ShoppingCart as SalesIcon,
  Inventory as InventoryIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
} from "@mui/icons-material";

// Função para formatar moeda
const formatCurrency = (value) => 
  new Intl.NumberFormat('pt-MZ', {
    style: 'currency',
    currency: 'MZN',
    minimumFractionDigits: 2,
  }).format(value).replace('MZN', 'MT');

// Componente de Abas
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`financas-tabpanel-${index}`}
      aria-labelledby={`financas-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function Financas() {
  const [tabValue, setTabValue] = useState(0);
  const [openVendaModal, setOpenVendaModal] = useState(false);
  const [vendas, setVendas] = useState([]);
  const [novaVenda, setNovaVenda] = useState({
    id: null,
    cliente: "",
    produto: "",
    quantidade: 1,
    precoUnitario: 0,
    formaPagamento: "dinheiro",
    data: new Date().toISOString().split('T')[0],
  });

  // Dados de exemplo
  const produtos = [
    { id: 1, nome: "Cabelo Natural 14'", preco: 4300, stock: 5 },
    { id: 2, nome: "Xiaomi Redmi", preco: 10500, stock: 8 },
    { id: 3, nome: "Dell Latitude 7280", preco: 16500, stock: 0 },
  ];

  // Carregar vendas do localStorage
  useEffect(() => {
    const vendasSalvas = localStorage.getItem('vendas');
    if (vendasSalvas) {
      setVendas(JSON.parse(vendasSalvas));
    } else {
      // Dados de exemplo
      const vendasExemplo = [
        {
          id: 1,
          cliente: "Maria João",
          produto: "Xiaomi Redmi",
          quantidade: 1,
          precoUnitario: 10500,
          total: 10500,
          formaPagamento: "dinheiro",
          data: "2024-01-15",
          hora: "14:30",
        },
        {
          id: 2,
          cliente: "Carlos Silva",
          produto: "Cabelo Natural 14'",
          quantidade: 2,
          precoUnitario: 4300,
          total: 8600,
          formaPagamento: "mbway",
          data: "2024-01-15",
          hora: "10:15",
        },
        {
          id: 3,
          cliente: "Empresa ABC",
          produto: "Dell Latitude 7280",
          quantidade: 1,
          precoUnitario: 16500,
          total: 16500,
          formaPagamento: "transferencia",
          data: "2024-01-14",
          hora: "16:45",
        },
      ];
      setVendas(vendasExemplo);
      localStorage.setItem('vendas', JSON.stringify(vendasExemplo));
    }
  }, []);

  // Salvar vendas no localStorage
  useEffect(() => {
    if (vendas.length > 0) {
      localStorage.setItem('vendas', JSON.stringify(vendas));
    }
  }, [vendas]);

  // Estatísticas
  const hoje = new Date().toISOString().split('T')[0];
  const vendasHoje = vendas.filter(venda => venda.data === hoje);
  const totalVendasHoje = vendasHoje.reduce((acc, venda) => acc + venda.total, 0);
  const totalVendasGeral = vendas.reduce((acc, venda) => acc + venda.total, 0);
  
  // Vendas por forma de pagamento
  const pagamentosStats = vendas.reduce((acc, venda) => {
    acc[venda.formaPagamento] = (acc[venda.formaPagamento] || 0) + venda.total;
    return acc;
  }, {});

  // Produtos mais vendidos
  const produtosStats = vendas.reduce((acc, venda) => {
    acc[venda.produto] = (acc[venda.produto] || 0) + venda.quantidade;
    return acc;
  }, {});

  const handleOpenVenda = () => {
    setNovaVenda({
      id: null,
      cliente: "",
      produto: "",
      quantidade: 1,
      precoUnitario: 0,
      formaPagamento: "dinheiro",
      data: new Date().toISOString().split('T')[0],
    });
    setOpenVendaModal(true);
  };

  const handleSaveVenda = () => {
    if (!novaVenda.cliente || !novaVenda.produto || !novaVenda.quantidade) return;

    const produtoSelecionado = produtos.find(p => p.nome === novaVenda.produto);
    const precoUnitario = produtoSelecionado ? produtoSelecionado.preco : novaVenda.precoUnitario;
    const total = precoUnitario * novaVenda.quantidade;

    const venda = {
      id: novaVenda.id || Date.now(),
      cliente: novaVenda.cliente,
      produto: novaVenda.produto,
      quantidade: parseInt(novaVenda.quantidade),
      precoUnitario: precoUnitario,
      total: total,
      formaPagamento: novaVenda.formaPagamento,
      data: novaVenda.data,
      hora: new Date().toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' }),
    };

    if (novaVenda.id) {
      // Editar venda existente
      setVendas(vendas.map(v => v.id === novaVenda.id ? venda : v));
    } else {
      // Adicionar nova venda
      setVendas([venda, ...vendas]);
    }

    setOpenVendaModal(false);
  };

  const handleDeleteVenda = (id) => {
    if (window.confirm("Tem certeza que deseja eliminar esta venda?")) {
      setVendas(vendas.filter(v => v.id !== id));
    }
  };

  const handleProdutoChange = (produtoNome) => {
    const produto = produtos.find(p => p.nome === produtoNome);
    setNovaVenda({
      ...novaVenda,
      produto: produtoNome,
      precoUnitario: produto ? produto.preco : 0,
    });
  };

  // Função para obter cor do chip baseado na forma de pagamento
  const getPagamentoColor = (forma) => {
    switch (forma) {
      case 'dinheiro': return 'success';
      case 'mbway': return 'primary';
      case 'transferencia': return 'warning';
      case 'credito': return 'error';
      default: return 'default';
    }
  };

  // Função para obter label da forma de pagamento
  const getPagamentoLabel = (forma) => {
    switch (forma) {
      case 'dinheiro': return 'Dinheiro';
      case 'mbway': return 'MB Way';
      case 'transferencia': return 'Transferência';
      case 'credito': return 'Cartão Crédito';
      default: return forma;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* CABEÇALHO */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          💰 Gestão Financeira
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={handleOpenVenda}
        >
          Nova Venda
        </Button>
      </Box>

      {/* ESTATÍSTICAS PRINCIPAIS */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <MoneyIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography color="textSecondary" gutterBottom>
                Vendas Hoje
              </Typography>
              <Typography variant="h4" component="div" color="success.main">
                {formatCurrency(totalVendasHoje)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {vendasHoje.length} transações
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUpIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography color="textSecondary" gutterBottom>
                Total Geral
              </Typography>
              <Typography variant="h4" component="div" color="primary.main">
                {formatCurrency(totalVendasGeral)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {vendas.length} vendas totais
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <SalesIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography color="textSecondary" gutterBottom>
                Vendas Média/Dia
              </Typography>
              <Typography variant="h4" component="div" color="warning.main">
                {formatCurrency(totalVendasGeral / Math.max(vendas.length, 1))}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Valor médio por venda
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <InventoryIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
              <Typography color="textSecondary" gutterBottom>
                Produtos Vendidos
              </Typography>
              <Typography variant="h4" component="div" color="info.main">
                {vendas.reduce((acc, v) => acc + v.quantidade, 0)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Unidades totais
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ABAS PRINCIPAIS */}
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Vendas Diárias" />
          <Tab label="Relatórios" />
          <Tab label="Formas de Pagamento" />
        </Tabs>

        {/* ABA 1: VENDAS DIÁRIAS */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              📊 Vendas de Hoje ({new Date().toLocaleDateString('pt-PT')})
            </Typography>
            {vendasHoje.length === 0 ? (
              <Alert severity="info">
                Nenhuma venda registada hoje.
              </Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell><strong>Cliente</strong></TableCell>
                    <TableCell><strong>Produto</strong></TableCell>
                    <TableCell><strong>Qtd</strong></TableCell>
                    <TableCell><strong>Preço Unit.</strong></TableCell>
                    <TableCell><strong>Total</strong></TableCell>
                    <TableCell><strong>Pagamento</strong></TableCell>
                    <TableCell><strong>Hora</strong></TableCell>
                    <TableCell align="center"><strong>Ações</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vendasHoje.map((venda) => (
                    <TableRow key={venda.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon fontSize="small" color="action" />
                          {venda.cliente}
                        </Box>
                      </TableCell>
                      <TableCell>{venda.produto}</TableCell>
                      <TableCell>{venda.quantidade}</TableCell>
                      <TableCell>{formatCurrency(venda.precoUnitario)}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'success.main' }}>
                        {formatCurrency(venda.total)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getPagamentoLabel(venda.formaPagamento)}
                          size="small"
                          color={getPagamentoColor(venda.formaPagamento)}
                        />
                      </TableCell>
                      <TableCell>{venda.hora}</TableCell>
                      <TableCell align="center">
                        <IconButton 
                          color="error" 
                          size="small"
                          onClick={() => handleDeleteVenda(venda.id)}
                          title="Eliminar Venda"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            📈 Histórico de Vendas
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Data</strong></TableCell>
                <TableCell><strong>Cliente</strong></TableCell>
                <TableCell><strong>Produto</strong></TableCell>
                <TableCell><strong>Qtd</strong></TableCell>
                <TableCell><strong>Total</strong></TableCell>
                <TableCell><strong>Pagamento</strong></TableCell>
                <TableCell align="center"><strong>Ações</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vendas.slice(0, 10).map((venda) => (
                <TableRow key={venda.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarIcon fontSize="small" color="action" />
                      {new Date(venda.data).toLocaleDateString('pt-PT')}
                    </Box>
                  </TableCell>
                  <TableCell>{venda.cliente}</TableCell>
                  <TableCell>{venda.produto}</TableCell>
                  <TableCell>{venda.quantidade}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {formatCurrency(venda.total)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getPagamentoLabel(venda.formaPagamento)}
                      size="small"
                      color={getPagamentoColor(venda.formaPagamento)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton 
                      color="error" 
                      size="small"
                      onClick={() => handleDeleteVenda(venda.id)}
                      title="Eliminar Venda"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabPanel>

        {/* ABA 2: RELATÓRIOS */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  🏆 Produtos Mais Vendidos
                </Typography>
                {Object.entries(produtosStats).length === 0 ? (
                  <Alert severity="info">
                    Nenhum dado de vendas disponível.
                  </Alert>
                ) : (
                  Object.entries(produtosStats)
                    .sort((a, b) => b[1] - a[1])
                    .map(([produto, quantidade]) => (
                      <Box key={produto} sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1">{produto}</Typography>
                        <Chip label={`${quantidade} uni`} color="primary" variant="outlined" />
                      </Box>
                    ))
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  📅 Vendas por Dia (Últimos 7 Dias)
                </Typography>
                <Alert severity="info">
                  Funcionalidade em desenvolvimento. Em breve terá gráficos detalhados.
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* ABA 3: FORMAS DE PAGAMENTO */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            💳 Distribuição por Forma de Pagamento
          </Typography>
          <Grid container spacing={3}>
            {Object.entries(pagamentosStats).map(([forma, total]) => (
              <Grid item xs={12} sm={6} md={3} key={forma}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Chip
                      label={getPagamentoLabel(forma)}
                      color={getPagamentoColor(forma)}
                      sx={{ mb: 2 }}
                    />
                    <Typography variant="h5" component="div">
                      {formatCurrency(total)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {((total / totalVendasGeral) * 100).toFixed(1)}% do total
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>
      </Paper>

      {/* MODAL DE NOVA VENDA */}
      <Dialog 
        open={openVendaModal} 
        onClose={() => setOpenVendaModal(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon />
            {novaVenda.id ? 'Editar Venda' : 'Registrar Nova Venda'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cliente"
                value={novaVenda.cliente}
                onChange={(e) => setNovaVenda({ ...novaVenda, cliente: e.target.value })}
                sx={{ mb: 2 }}
                required
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Produto</InputLabel>
                <Select
                  value={novaVenda.produto}
                  label="Produto"
                  onChange={(e) => handleProdutoChange(e.target.value)}
                >
                  {produtos.map(produto => (
                    <MenuItem key={produto.id} value={produto.nome}>
                      {produto.nome} - {formatCurrency(produto.preco)}
                    </MenuItem>
                  ))}
                  <MenuItem value="outro">Outro Produto</MenuItem>
                </Select>
              </FormControl>

              {novaVenda.produto === "outro" && (
                <TextField
                  fullWidth
                  label="Preço Unitário Personalizado (MT)"
                  type="number"
                  value={novaVenda.precoUnitario}
                  onChange={(e) => setNovaVenda({ ...novaVenda, precoUnitario: e.target.value })}
                  sx={{ mb: 2 }}
                />
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Quantidade"
                type="number"
                value={novaVenda.quantidade}
                onChange={(e) => setNovaVenda({ ...novaVenda, quantidade: e.target.value })}
                sx={{ mb: 2 }}
                required
                inputProps={{ min: "1" }}
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Forma de Pagamento</InputLabel>
                <Select
                  value={novaVenda.formaPagamento}
                  label="Forma de Pagamento"
                  onChange={(e) => setNovaVenda({ ...novaVenda, formaPagamento: e.target.value })}
                >
                  <MenuItem value="dinheiro">Dinheiro</MenuItem>
                  <MenuItem value="mbway">MB Way</MenuItem>
                  <MenuItem value="transferencia">Transferência</MenuItem>
                  <MenuItem value="credito">Cartão de Crédito</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Data da Venda"
                type="date"
                value={novaVenda.data}
                onChange={(e) => setNovaVenda({ ...novaVenda, data: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          {novaVenda.produto && novaVenda.quantidade && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>Total da Venda:</strong> {formatCurrency(
                (novaVenda.precoUnitario || produtos.find(p => p.nome === novaVenda.produto)?.preco || 0) * 
                novaVenda.quantidade
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenVendaModal(false)}>
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveVenda}
            disabled={!novaVenda.cliente || !novaVenda.produto || !novaVenda.quantidade}
          >
            {novaVenda.id ? 'Atualizar' : 'Registrar'} Venda
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}